import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Select, Input, Button, notification } from "antd";
import { EditOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import ProductService from "services/product";
import "./ProductCategorySelect.scss";

const { Option } = Select;

const ProductCategorySelect = ({
  value,
  onChange,
  placeholder,
  categories = [],
  onReloadCategories,
  onCheckUsage,
  ...props
}) => {
  const { t } = useTranslation();

  const [searchValue, setSearchValue] = useState("");
  const [filteredCategories, setFilteredCategories] = useState(categories);
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const debounceTimer = useRef(null);
  const currentSearchId = useRef(null);

  useEffect(() => {
    setFilteredCategories(categories);
  }, [categories]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const hasMatchingCategory = useMemo(() =>
    filteredCategories.some(category =>
      (category.productCategoryName || category.categoryName || category.name)
        ?.toLowerCase() === searchInputValue.trim().toLowerCase()
    ), [filteredCategories, searchInputValue]);

  const handleSearch = useCallback(async (value) => {
    setSearchValue(value);
    setSearchInputValue(value);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    const searchId = Date.now();
    currentSearchId.current = searchId;

    debounceTimer.current = setTimeout(async () => {
      if (currentSearchId.current !== searchId) return;

      try {
        const response = await ProductService.categoryAdvanceUserGetList({
          limit: 100,
          searchText: value || undefined,
          includeSystem: true,
        });
        const list = Array.isArray(response) ? response : response?.data || [];
        setFilteredCategories(list);
      } catch (error) {
        setFilteredCategories(categories);
      }
    }, 300);
  }, []);

  const handleCreateCategory = async () => {
    if (!searchInputValue.trim()) return;

    if (categories.length >= 100) {
      notification.error({
        message: "",
        description: t("product.categoryLimitReached", { limit: 100 }),
      });
      return;
    }

    try {
      setLoading(true);
      const response = await ProductService.categoryAdvanceUserCreate({
        productCategoryName: searchInputValue.trim(),
      });
      if (response?.statusCode === 200) {
        notification.success({
          message: "",
          description: t("product.categoryAddSuccess", { name: searchInputValue.trim() }),
        });
        const newCategoryId = response.data?.productCategoryId || response.data?.id;
        if (newCategoryId && onChange) {
          onChange(newCategoryId);
        }
        setSearchInputValue("");
        handleClear();
        if (onReloadCategories) {
          onReloadCategories();
        }
      } else {
        notification.error({
          message: "",
          description: t("product.categoryAddFail"),
        });
      }
    } catch (error) {
      console.error("Failed to create category:", error);
      notification.error({
        message: "",
        description: t("product.categoryAddError"),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditStart = (category) => {
    setEditingId(category.productCategoryId || category.categoryId || category.id);
    setEditingValue(category.productCategoryName || category.categoryName || category.name);
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditingValue("");
  };

  const handleEditSave = async () => {
    if (!editingValue.trim()) return;
    try {
      setLoading(true);
      const response = await ProductService.categoryAdvanceUserUpdateBy({
        id: editingId,
        data: { productCategoryName: editingValue.trim() },
      });
      if (response?.statusCode === 200) {
        notification.success({
          message: "",
          description: t("product.categoryUpdateSuccess"),
        });
        await handleSearch(searchValue);
        setEditingId(null);
        setEditingValue("");
        if (onReloadCategories) {
          onReloadCategories();
        }
      }
    } catch (error) {
      console.error("Failed to update category:", error);
      notification.error({
        message: "",
        description: t("product.categoryUpdateError"),
      });
    } finally {
      setLoading(false);
    }
  };



  const dropdownRender = (menu) => {
    return (
      <div>
        {searchInputValue.trim() ? menu : (
          filteredCategories.length > 0 ? menu : (
            <div className="pcs-dropdown-empty">
              {t("product.noCategories")}
            </div>
          )
        )}
        {searchInputValue.trim() && !hasMatchingCategory && (
          <div
            className="pcs-add-option"
            onClick={(e) => {
              e.stopPropagation();
              handleCreateCategory();
            }}
          >
            {t("product.addOption")} <strong>'{searchInputValue}'</strong>
          </div>
        )}
      </div>
    );
  };

  const optionRender = (category) => {
    const categoryId = category.productCategoryId || category.categoryId || category.id;
    const isCurrentlyEditing = editingId === categoryId;
    const categoryName = category.productCategoryName || category.categoryName || category.name;

    if (isCurrentlyEditing) {
      return (
        <div style={{ display: "flex", width: "100%" }}>
          <Input
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onPressEnter={handleEditSave}
            className="pcs-search-input"
            autoFocus
          />
          <div className="pcs-action-buttons">
            <Button
              type="link"
              icon={<CheckCircleOutlined />}
              onClick={handleEditSave}
              loading={loading}
              size="small"
              className="pcs-action-button"
            />
          </div>
        </div>
      );
    }

    return (
      <div className="pcs-category-item">
        <div className="pcs-search-input">{categoryName}</div>
        <div className="pcs-action-buttons">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleEditStart(category);
            }}
            size="small"
            className="pcs-action-button"
          />
        </div>
      </div>
    );
  };

  const handleDropdownVisibleChange = (visible) => {
    if (!visible && editingId) {
      handleEditCancel();
    }
  };

  const handleSelect = (selectedValue) => {
    if (editingId) {
      handleEditCancel();
    }
  };

  const handleClear = () => {
    setSearchValue("");
    setSearchInputValue("");
    setFilteredCategories(categories);
  };

  return (
    <Select
      value={value}
      onChange={onChange}
      placeholder={placeholder || t("product.selectCategory")}
      showSearch
      searchValue={searchInputValue}
      onSearch={handleSearch}
      filterOption={false}
      dropdownRender={dropdownRender}
      allowClear={!!value}
      onClear={handleClear}
      optionLabelProp="label"
      onDropdownVisibleChange={handleDropdownVisibleChange}
      onSelect={handleSelect}
      {...props}
    >
      {filteredCategories.map((category) => {
        const categoryName = category.productCategoryName || category.categoryName || category.name;
        return (
          <Option
            key={category.productCategoryId || category.categoryId || category.id}
            value={category.productCategoryId || category.categoryId || category.id}
            label={categoryName}
          >
            {optionRender(category)}
          </Option>
        );
      })}
      {searchInputValue.trim() && filteredCategories.length === 0 && !filteredCategories.some(category => 
        (category.productCategoryName || category.categoryName || category.name)?.toLowerCase() === searchInputValue.trim().toLowerCase()
      ) && (
        <Option key="placeholder-option" value="placeholder" label="" style={{ display: 'none' }}>
        </Option>
      )}
    </Select>
  );
};

export default ProductCategorySelect;
